import { EntityState } from '@ngrx/entity';
import {
  patchState,
  signalStore,
  type,
  withMethods,
  withState,
} from '@ngrx/signals';
import { IAlertItem, TAnswerType } from '../entities/alerts.interface';
import {
  removeEntity,
  withEntities,
  updateEntity,
  addEntity,
  entityConfig,
} from '@ngrx/signals/entities';
import { v4 as uuidv4 } from 'uuid';
import { Observable } from 'rxjs';
import { Injector, Signal, effect, untracked } from '@angular/core';

export interface IAlertStore {
  alertList: EntityState<IAlertItem>;
}

export const initialAlertStore: IAlertStore = {
  alertList: null,
};


const alertItemConfig = entityConfig({
  entity: type<IAlertItem>(),
  collection: 'alertList',
  selectId: (alert: IAlertItem) => alert.id,
});

const addAlertEntity = (store, alert) => {
  patchState(store, addEntity(alert, alertItemConfig));
};

const handleAlertResponse = (store, alert, observer) => {
  const currentAlert = store.alertListEntityMap()[alert.id];

  if (!currentAlert) {
    observer.complete();
    return;
  }

  if (currentAlert.answer !== undefined) {
    observer.next(currentAlert.answer);
    observer.complete();
    patchState(store, removeEntity(alert.id, alertItemConfig));
  }
};

const createResponseObservable = (store, alert): Observable<TAnswerType> => {
  return new Observable((observer) => {
      handleAlertResponse(store, alert, observer);
  });
};

const setupSignalEffects = (store, alert: IAlertItem, signalSource: Signal<any>[], injector: Injector) => {
  signalSource.forEach(signal => {
    effect(() => {
      if (signal()) {
        untracked(() => {
          if(typeof signal() === 'string') alert.message = signal();
          addAlertEntity(store, alert)
        });
      }
    }, { injector });
  });
};

export const AlertStore = signalStore(
  { providedIn: 'root' },
  withEntities(alertItemConfig),
  withState<IAlertStore>(initialAlertStore),
  withMethods((store) => {
    const remove = (id: string) =>
      patchState(store, removeEntity(id, alertItemConfig));

    return {
      createAlertBySignal(payload: IAlertItem, signalSource: Signal<any>[], injector: Injector): Observable<TAnswerType> {
        const alert: IAlertItem = { ...payload, id: uuidv4() };
        setupSignalEffects(store, alert, signalSource, injector);
        return createResponseObservable(store, alert);
      },

      create(payload: IAlertItem): Observable<TAnswerType> {
        const alert: IAlertItem = { ...payload, id: uuidv4() };
        addAlertEntity(store, alert);
        return createResponseObservable(store, alert);
      },

      removeAlert(id: string): void {
        remove(id);
      },

      confirm(id: string): void {
        patchState(
          store,
          updateEntity({ id, changes: { answer: true } }, alertItemConfig)
        );
      },

      cancel(id: string): void {
        patchState(
          store,
          updateEntity({ id, changes: { answer: false } }, alertItemConfig)
        );
      },

      selectOption(
        id: string,
        answer: 'secondaryButton' | 'primaryButton'
      ): void {
        patchState(
          store,
          updateEntity({ id, changes: { answer } }, alertItemConfig)
        );
      },

      genericSuccess(): void {
        this.create({
          type: 'success',
          title: 'ALERT.TITLE_SUCCESS',
          message: 'ALERT.SUCCESS',
        });
      },

      genericError(): void {
        this.create({
          type: 'warning',
          title: 'ALERT.GENERIC_ERROR_TITLE',
          message: 'ALERT.GENERIC_ERROR_MESSAGE',
        });
      },
    };
  })
);
